import React, { useCallback, useEffect, useRef, useState } from 'react';

import { getPartnerClients, getPartnerScore } from '../services/partner.service';
import { toUserFacingErrorMessage } from '../services/_core';
import PartnerDashboard from './PartnerDashboard';

const SAFE_DEFAULTS = {
  total_clients: 0,
  mrr_influenced: 0,
  commission_earned: 0,
  score: 0,
  tier: 'Bronze',
  breakdown: {
    scale: 0,
    revenue: 0,
    security_impact: 0,
    engagement: 0,
    retention: 0,
  },
};

const TIER_ORDER = ['Bronze', 'Silver', 'Gold'];

function clampNumber(value, { min = 0, max = Number.POSITIVE_INFINITY } = {}) {
  const num = Number(value);
  if (!Number.isFinite(num)) return min;
  return Math.max(min, Math.min(max, num));
}

function normalizeTier(value) {
  const tier = String(value || '').trim().toLowerCase();
  if (tier === 'gold') return 'Gold';
  if (tier === 'silver') return 'Silver';
  return 'Bronze';
}

function flattenBreakdownMetric(entry) {
  if (entry == null) return 0;
  if (typeof entry === 'number') return clampNumber(entry);
  if (typeof entry === 'object') {
    const v = entry.weighted_score ?? entry.normalized_score ?? entry.score;
    return clampNumber(Number(v));
  }
  return 0;
}

function normalizeBreakdown(value) {
  const src = value && typeof value === 'object' ? value : {};
  return {
    scale: flattenBreakdownMetric(src.scale),
    revenue: flattenBreakdownMetric(src.revenue),
    security_impact: flattenBreakdownMetric(src.security_impact),
    engagement: flattenBreakdownMetric(src.engagement),
    retention: flattenBreakdownMetric(src.retention),
  };
}

function getTierProgress(score, tier, payload = {}) {
  const normalizedScore = clampNumber(score, { min: 0, max: 100 });
  const normalizedTier = normalizeTier(tier);
  const req = payload?.next_tier_requirements;
  const ladderNext =
    normalizedTier === 'Bronze' ? 'Silver' : normalizedTier === 'Silver' ? 'Gold' : null;
  const apiNextRaw =
    req && typeof req === 'object' && 'next_tier' in req ? req.next_tier : payload?.next_tier;
  let nextTier = ladderNext;
  if (apiNextRaw != null && String(apiNextRaw).trim() !== '') {
    nextTier = normalizeTier(apiNextRaw);
  }
  const thresholds = {
    Bronze: { min: 0, max: 49 },
    Silver: { min: 50, max: 79 },
    Gold: { min: 80, max: 100 },
  };

  const defaultProgress =
    normalizedTier === 'Bronze'
      ? clampNumber((normalizedScore / 50) * 100, { min: 0, max: 100 })
      : normalizedTier === 'Silver'
        ? clampNumber(((normalizedScore - 50) / 30) * 100, { min: 0, max: 100 })
        : 100;

  const nextTierProgressRaw =
    payload?.next_tier_progress ??
    payload?.progress_to_next_tier ??
    (req && typeof req === 'object' ? req.progress_to_next_tier : undefined);
  const nextTierProgress = clampNumber(
    nextTierProgressRaw == null ? defaultProgress : nextTierProgressRaw,
    { min: 0, max: 100 }
  );

  const pointsToNextTier =
    nextTier == null
      ? 0
      : clampNumber(
          payload?.points_to_next_tier ??
            payload?.score_to_next_tier ??
            (req && typeof req === 'object' ? req.points_needed : undefined) ??
            (thresholds[nextTier] ? thresholds[nextTier].min - normalizedScore : 0),
          { min: 0, max: 10000 }
        );

  return {
    current_tier: normalizedTier,
    next_tier: nextTier,
    next_tier_progress: nextTierProgress,
    points_to_next_tier: pointsToNextTier,
    tier_order: TIER_ORDER,
  };
}

function normalizePartnerDashboardPayload(payload) {
  const src = payload && typeof payload === 'object' ? payload : {};
  const metrics = src.metrics && typeof src.metrics === 'object' ? src.metrics : {};
  const tierRaw =
    src.tier ??
    (src.current_tier && typeof src.current_tier === 'object' ? src.current_tier.name : src.current_tier);
  const tier = normalizeTier(tierRaw ?? SAFE_DEFAULTS.tier);
  const score = clampNumber(src.score ?? src.partner_score ?? SAFE_DEFAULTS.score, { min: 0, max: 100 });
  const commissionRateRaw = Number(
    src.commission_rate ??
      (src.current_tier && typeof src.current_tier === 'object' ? src.current_tier.commission_rate : undefined)
  );
  const normalized = {
    total_clients: clampNumber(src.total_clients ?? metrics.total_clients ?? SAFE_DEFAULTS.total_clients),
    mrr_influenced: clampNumber(src.mrr_influenced ?? metrics.mrr ?? SAFE_DEFAULTS.mrr_influenced),
    commission_earned: clampNumber(src.commission_earned ?? SAFE_DEFAULTS.commission_earned),
    commission_pending: src.commission_pending == null ? null : clampNumber(src.commission_pending),
    commission_rate: Number.isFinite(commissionRateRaw) && commissionRateRaw > 0 ? commissionRateRaw : null,
    referral_code: String(src.referral_code || '').trim(),
    partner_name: String(src.partner_name || '').trim(),
    score,
    tier,
    breakdown: normalizeBreakdown(src.breakdown ?? SAFE_DEFAULTS.breakdown),
  };

  const tierHints = {
    ...src,
    points_to_next_tier:
      src.points_to_next_tier ??
      src.score_to_next_tier ??
      src.next_tier_requirements?.points_needed,
    next_tier_progress:
      src.next_tier_progress ??
      src.progress_to_next_tier ??
      src.next_tier_requirements?.progress_to_next_tier,
  };

  return {
    ...normalized,
    ...getTierProgress(score, tier, tierHints),
  };
}

export default function PartnerScoreCard({ partnerId = null, orgQuery = null, themeLight = true }) {
  const [data, setData] = useState(() => normalizePartnerDashboardPayload(null));
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const mountedRef = useRef(true);

  const loadPartnerDashboard = useCallback(async () => {
    if (!mountedRef.current) return;

    setLoading(true);
    setError('');

    try {
      const query = {
        ...(orgQuery && typeof orgQuery === 'object' ? orgQuery : {}),
        ...(partnerId ? { partner_id: partnerId } : {}),
      };
      // The client list is secondary: a failure there must not blank the scorecard.
      const [payload, clientList] = await Promise.all([
        getPartnerScore(null, query),
        Promise.resolve()
          .then(() => getPartnerClients(null, query))
          .catch(() => []),
      ]);
      if (!mountedRef.current) return;
      setData(normalizePartnerDashboardPayload(payload));
      setClients(Array.isArray(clientList) ? clientList : []);
    } catch (err) {
      if (!mountedRef.current) return;
      const status = Number(err?.status);
      setClients([]);

      if (status === 401) {
        setData(normalizePartnerDashboardPayload(null));
        setError('You do not have partner access.');
        return;
      }
      if (status === 403) {
        // The backend explains why (e.g. login not linked to a partner yet).
        setData(normalizePartnerDashboardPayload(null));
        setError(toUserFacingErrorMessage(err?.message, 'You do not have partner access.'));
        return;
      }

      setData(normalizePartnerDashboardPayload(null));
      const isServerError = Number.isFinite(status) && status >= 500;
      if (isServerError) {
        setError('Unable to load partner dashboard right now. Please try again.');
        return;
      }
      setError(
        toUserFacingErrorMessage(
          err?.message,
          'Unable to load partner dashboard right now. Please try again.'
        )
      );
    } finally {
      if (!mountedRef.current) return;
      setLoading(false);
    }
  }, [orgQuery, partnerId]);

  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      await loadPartnerDashboard();
    })();
    return () => {
      mountedRef.current = false;
    };
  }, [loadPartnerDashboard]);

  return (
    <PartnerDashboard
      data={data}
      clients={clients}
      loading={loading}
      error={error}
      onRetry={loadPartnerDashboard}
      themeLight={themeLight}
    />
  );
}
